import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BarChart3, FileText, TrendingUp, IndianRupee, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const CATEGORY_ICON = {
  A: FileText,
  B: BarChart3,
  C: TrendingUp,
  D: IndianRupee,
};
const CATEGORY_ACCENT = {
  A: "text-sky-700 bg-sky-50 border-sky-200",
  B: "text-violet-700 bg-violet-50 border-violet-200",
  C: "text-amber-700 bg-amber-50 border-amber-200",
  D: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

export default function Reports() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["reports-catalog"],
    queryFn: async () => (await api.get("/reports/catalog")).data,
  });

  const categories = data?.categories || [];

  return (
    <div className="p-8" data-testid="reports-home">
      <div className="label-eyebrow">Reports</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Reports</h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        Filterable, exportable views across inventory, lead generation, pipeline and revenue.
      </p>

      {isLoading ? (
        <div className="mt-8 text-sm text-slate-500">Loading…</div>
      ) : categories.length === 0 ? (
        <div className="mt-8 text-sm text-slate-500" data-testid="reports-empty">No reports available for your role.</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {categories.map((cat) => {
            const Icon = CATEGORY_ICON[cat.key] || FileText;
            return (
              <div key={cat.key} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`report-category-${cat.key}`}>
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                  <span className={`inline-flex h-9 w-9 items-center justify-center rounded-md border ${CATEGORY_ACCENT[cat.key]}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="label-eyebrow !text-slate-400">Category {cat.key}</div>
                    <div className="font-display text-lg font-semibold text-slate-900 leading-tight">{cat.title}</div>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {cat.reports.map((r) => (
                    <button
                      key={r.key}
                      onClick={() => navigate(`/reports/${r.key}`)}
                      className="w-full text-left px-5 py-3.5 hover:bg-slate-50 transition-colors flex items-center justify-between group"
                      data-testid={`report-link-${r.key}`}
                    >
                      <div className="pr-4">
                        <div className="text-sm font-semibold text-slate-900">{r.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-600 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
